/** Shared keyword search + column sort helpers for TodayOrder tables. */

function str(v) {
    return v != null ? String(v).trim() : '';
}

function foodItemsText(order) {
    const items = order.orderLineItems || order.OrderLineItems || order.lineItems || [];
    if (!Array.isArray(items) || items.length === 0) return '';
    return items
        .map(
            (item) =>
                item.foodItemName ||
                item.Food_Item_Name ||
                item.FoodItemName ||
                item.itemName ||
                ''
        )
        .filter(Boolean)
        .join(' ');
}

function paymentMethodText(order) {
    const payment =
        order?.paymentMethod ??
        order?.Payment_Method ??
        order?.payment_method ??
        order?.payment?.paymentMethod ??
        order?.payment?.payment_method ??
        order?.payment?.PaymentMethod ??
        '';
    if (Array.isArray(payment)) {
        return payment.map((m) => String(m)).join(' ');
    }
    return String(payment);
}

function parseDateSortValue(raw) {
    if (!raw) return 0;
    const s = String(raw).trim();
    if (!s) return 0;
    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s) ? s.replace(' ', 'T') : s;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function compareValues(va, vb, direction) {
    let cmp = 0;
    if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
    } else {
        cmp = String(va).localeCompare(String(vb), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    }
    return direction === 'asc' ? cmp : -cmp;
}

export function orderMatchesTableKeyword(order, keyword, tableKind, extras = {}) {
    const q = String(keyword ?? '').trim().toLowerCase();
    if (!q) return true;
    const haystack = buildOrderSearchHaystack(order, tableKind, extras).toLowerCase();
    return haystack.includes(q);
}

function buildOrderSearchHaystack(order, tableKind, extras = {}) {
    const orderId = str(order.orderId ?? order.orderNumber ?? order.cateringOrderId);
    const parts = [orderId];

    if (tableKind === 'dineIn') {
        parts.push(
            str(order.tableNumber ?? order.table_number ?? order.TableNumber ?? order.table_no ?? order.tableNo),
            foodItemsText(order),
            str(order.totalPrice),
            str(order.orderStatus ?? order.OrderStatus),
            str(order.paymentStatus),
            paymentMethodText(order),
            str(order.mobile ?? order.customerMobile),
            str(order.customerName ?? order.CustomerName),
            str(order.createdDate ?? order.createdAt)
        );
    } else if (tableKind === 'catering') {
        parts.push(
            str(order.customerName ?? order.CustomerName),
            str(order.mobile ?? order.customerMobile ?? order.contactNumber),
            str(order.altNumber ?? order.Alt_Number),
            str(order.eventDate),
            str(order.eventType ?? order.EventType),
            str(order.eventLocation ?? order.EventLocation ?? order.deliveryAddress),
            foodItemsText(order),
            str(order.numberOfGuests ?? order.NumberOfGuests ?? order.number_of_guests),
            str(order.totalPrice),
            str(order.advancePayment ?? order.advance_payment),
            str(order.pendingAmount ?? order.pending_amount),
            str(order.orderStatus ?? order.OrderStatus),
            paymentMethodText(order),
            str(order.createdDate ?? order.createdAt)
        );
    } else if (tableKind === 'jreats') {
        const child = extras.getJreatsChildSchoolDisplay?.(order) ?? {};
        parts.push(
            str(extras.formatJreatsMealType?.(order)),
            foodItemsText(order),
            str(order.totalPrice ?? order.TotalPrice),
            str(order.billingType ?? order.billing_type),
            str(order.restaurantSettlementAmount ?? order.restaurant_settlement_amount),
            str(order.creditsConsumed ?? order.credits_consumed),
            str(order.organizationName ?? order.OrganizationName),
            str(child.className),
            str(child.section),
            str(child.rollNumber),
            str(order.receiverName ?? order.ReceiverName),
            str(order.orderStatus ?? order.OrderStatus),
            str(extras.assignedAgents?.[orderId]),
            str(order.createdDate ?? order.CreatedDate)
        );
    } else {
        parts.push(
            str(order.customerName ?? order.CustomerName),
            foodItemsText(order),
            str(order.totalPrice ?? order.TotalPrice),
            str(order.orderStatus ?? order.OrderStatus),
            str(order.createdDate ?? order.CreatedDate),
            str(order.deliveryTime ?? order.DeliveryTime ?? order.Delivery_Time),
            str(order.organizationName ?? order.OrganizationName),
            str(order.receiverName ?? order.ReceiverName),
            str(extras.assignedAgents?.[orderId])
        );
    }

    return parts.filter(Boolean).join(' ');
}

export function getOrderSortValue(order, sortKey, tableKind, extras = {}) {
    switch (sortKey) {
        case 'orderId':
            return str(order.orderId ?? order.orderNumber ?? order.cateringOrderId);
        case 'tableNumber':
            return str(
                order.tableNumber ??
                    order.table_number ??
                    order.TableNumber ??
                    order.table_no ??
                    order.tableNo
            );
        case 'foodItems':
            return foodItemsText(order);
        case 'price':
        case 'totalPrice':
            return Number.parseFloat(order.totalPrice ?? order.TotalPrice) || 0;
        case 'orderStatus':
        case 'status':
            return str(order.orderStatus ?? order.OrderStatus).toUpperCase();
        case 'paymentStatus':
            return str(order.paymentStatus).toUpperCase();
        case 'paymentMethod':
            return paymentMethodText(order).toUpperCase();
        case 'mobile':
        case 'whatsapp':
            return str(order.mobile ?? order.customerMobile ?? order.contactNumber);
        case 'createdDate':
            return parseDateSortValue(order.createdDate ?? order.createdAt ?? order.CreatedDate);
        case 'customerName':
            return str(order.customerName ?? order.CustomerName);
        case 'altNumber':
            return str(order.altNumber ?? order.Alt_Number);
        case 'eventDate':
            return parseDateSortValue(order.eventDate);
        case 'eventType':
            return str(order.eventType ?? order.EventType);
        case 'eventLocation':
            return str(order.eventLocation ?? order.EventLocation ?? order.deliveryAddress);
        case 'numberOfGuests':
            return Number.parseFloat(order.numberOfGuests ?? order.NumberOfGuests ?? order.number_of_guests) || 0;
        case 'advancePayment':
            return Number.parseFloat(order.advancePayment ?? order.advance_payment) || 0;
        case 'pendingAmount': {
            const pending = order.pendingAmount ?? order.pending_amount;
            if (pending != null) return Number.parseFloat(pending) || 0;
            const total = Number.parseFloat(order.totalPrice) || 0;
            const advance = Number.parseFloat(order.advancePayment ?? order.advance_payment) || 0;
            return Math.max(0, total - advance);
        }
        case 'mealType':
            return str(extras.formatJreatsMealType?.(order));
        case 'billingType':
            return str(order.billingType ?? order.billing_type).toUpperCase();
        case 'school':
            return str(order.organizationName ?? order.OrganizationName);
        case 'class':
            return str(extras.getJreatsChildSchoolDisplay?.(order)?.className);
        case 'section':
            return str(extras.getJreatsChildSchoolDisplay?.(order)?.section);
        case 'rollNo':
            return str(extras.getJreatsChildSchoolDisplay?.(order)?.rollNumber);
        case 'receiverName':
            return str(order.receiverName ?? order.ReceiverName);
        case 'assignedAgent': {
            const agentOrderId = str(order.orderId ?? order.orderNumber);
            return str(extras.assignedAgents?.[agentOrderId]);
        }
        case 'deliveryTime':
            return str(order.deliveryTime ?? order.DeliveryTime ?? order.Delivery_Time);
        case 'organizationName':
            return str(order.organizationName ?? order.OrganizationName);
        default:
            return '';
    }
}

export function compareOrders(a, b, sortKey, direction, tableKind, extras = {}) {
    const va = getOrderSortValue(a, sortKey, tableKind, extras);
    const vb = getOrderSortValue(b, sortKey, tableKind, extras);
    return compareValues(va, vb, direction);
}

export function sortOrderList(list, sortState, tableKind, extras = {}) {
    if (!sortState?.key) {
        return [...list].sort(
            (a, b) =>
                parseDateSortValue(b.createdDate ?? b.createdAt ?? b.CreatedDate) -
                parseDateSortValue(a.createdDate ?? a.createdAt ?? a.CreatedDate)
        );
    }
    return [...list].sort((a, b) =>
        compareOrders(a, b, sortState.key, sortState.direction, tableKind, extras)
    );
}

export function buildSortHandlers(setSort, onPageReset) {
    const handleSortAsc = (key) => {
        onPageReset?.();
        setSort((prev) => {
            if (prev?.key === key && prev.direction === 'asc') return null;
            return { key, direction: 'asc' };
        });
    };
    const handleSortDesc = (key) => {
        onPageReset?.();
        setSort((prev) => {
            if (prev?.key === key && prev.direction === 'desc') return null;
            return { key, direction: 'desc' };
        });
    };
    return { handleSortAsc, handleSortDesc };
}
